import axios from "axios";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:3080/api";
export const STATIC_URL = API_URL.replace(/\/api\/?$/, "");
export const fileUrl = (folder, filename) =>
  filename ? `${STATIC_URL}/uploads/${folder}/${filename}` : "";

let _store = null;
let _logout = null;
let _setTokens = null;

export const injectStore = (store, actions) => {
  _store = store;
  _logout = actions.logout;
  _setTokens = actions.setTokens;
};

const api = axios.create({
  baseURL: API_URL,
  withCredentials: true,
  timeout: 20000,
});

// Keys that must keep their case as the user typed them. Everything else
// in a request body gets auto-uppercased so the backend stores ALL CAPS
// data — including when the user opens an old (lowercase) record and just
// hits Save.
const NO_UPPERCASE_KEYS = new Set([
  // Auth / credentials
  "password",
  "currentPassword",
  "newPassword",
  "confirmPassword",
  "email",
  "username",
  "token",
  "accessToken",
  "refreshToken",
  // IMEIs / device IDs
  "imei",
  "IMEI",
  "imei1",
  "motor_imei",
  "oht_imei",
  "motor_sim_number",
  "oht_sim_number",
  "confirmIMEI",
  // Phone / contact numbers (digits only, but keep verbatim)
  "phoneNumber1",
  "phoneNumber2",
  "operator_phone",
  "secretary_phone",
  "contactNumber",
  "contactName",
  // Enum / technical values that backend validates against fixed lists
  "status",
  "projecttype",
  "role",
  "device_type",
  "model",
  "outcome",
  "severity",
  "actionType",
  "entityType",
  "actor_role",
  "sort",
  "direction",
  // Coordinates / IDs
  "GST",
  "gst",
  "lat",
  "lng",
  "_id",
  "id",
  "entityId",
  "companyId",
  "deviceId",
  "userId",
  "responsiblePersonId",
  // Date strings
  "installation_date",
  "expiry_date",
  "createdAt",
  "updatedAt",
  "dateFrom",
  "dateTo",
]);

const upperizeBody = (val) => {
  if (val == null) return val;
  if (Array.isArray(val)) return val.map(upperizeBody);
  if (typeof val === "object") {
    const out = {};
    for (const [k, v] of Object.entries(val)) {
      if (NO_UPPERCASE_KEYS.has(k)) {
        out[k] = v;
      } else if (typeof v === "string") {
        out[k] = v.toUpperCase();
      } else if (v && typeof v === "object") {
        out[k] = upperizeBody(v);
      } else {
        out[k] = v;
      }
    }
    return out;
  }
  return val;
};

const upperizeFormData = (fd) => {
  const next = new FormData();
  for (const [k, v] of fd.entries()) {
    if (v instanceof File || v instanceof Blob) {
      next.append(k, v);
    } else if (NO_UPPERCASE_KEYS.has(k)) {
      next.append(k, v);
    } else if (typeof v === "string") {
      // JSON-stringified objects (e.g. responsiblePerson) — upperize parsed value
      const trimmed = v.trim();
      if (
        (trimmed.startsWith("{") && trimmed.endsWith("}")) ||
        (trimmed.startsWith("[") && trimmed.endsWith("]"))
      ) {
        try {
          const parsed = JSON.parse(trimmed);
          next.append(k, JSON.stringify(upperizeBody(parsed)));
          continue;
        } catch {
          /* fall through */
        }
      }
      next.append(k, v.toUpperCase());
    } else {
      next.append(k, v);
    }
  }
  return next;
};

api.interceptors.request.use((config) => {
  const token = _store?.getState().auth.accessToken;
  if (token) config.headers.Authorization = `Bearer ${token}`;

  // Auto-uppercase body for write methods only
  const method = (config.method || "get").toLowerCase();
  if (
    config.data &&
    ["post", "put", "patch"].includes(method) &&
    !config.skipUppercase // per-request opt-out
  ) {
    if (config.data instanceof FormData) {
      config.data = upperizeFormData(config.data);
    } else if (typeof config.data === "object") {
      config.data = upperizeBody(config.data);
    }
  }

  return config;
});

let isRefreshing = false;
let pendingQueue = [];

const processQueue = (error, token = null) => {
  pendingQueue.forEach((p) => (error ? p.reject(error) : p.resolve(token)));
  pendingQueue = [];
};

api.interceptors.response.use(
  (res) => res,
  async (error) => {
    const original = error.config;
    const status = error.response?.status;
    const refreshToken = _store?.getState().auth.refreshToken;

    if (
      status === 401 &&
      !original._retry &&
      refreshToken &&
      !original.url.includes("/auth/refresh")
    ) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          pendingQueue.push({ resolve, reject });
        }).then((token) => {
          original.headers.Authorization = `Bearer ${token}`;
          return api(original);
        });
      }

      original._retry = true;
      isRefreshing = true;

      try {
        const { data } = await axios.post(`${API_URL}/auth/refresh`, {
          refreshToken,
        });
        const { accessToken, refreshToken: newRefresh } = data.data;
        _store.dispatch(_setTokens({ accessToken, refreshToken: newRefresh }));
        processQueue(null, accessToken);
        original.headers.Authorization = `Bearer ${accessToken}`;
        return api(original);
      } catch (err) {
        processQueue(err, null);
        _store.dispatch(_logout());
        return Promise.reject(err);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

export default api;
